import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { imgUrl, artAlt } from "./ArtImageShared";

export const Lightbox = ({ artworks, startIndex, onClose, inquireBase = "/contact" }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, startIndex });
  const [current, setCurrent] = useState(startIndex);

  const onSelect = useCallback(() => {
    if (emblaApi) setCurrent(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") emblaApi?.scrollPrev();
      if (e.key === "ArrowRight") emblaApi?.scrollNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [emblaApi, onClose]);

  const art = artworks[current];
  const info = [art?.medium, art?.dimensions, art?.year].filter(Boolean).join(" · ");

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[90] bg-ink/[0.985] flex flex-col"
        data-testid="artwork-lightbox"
        role="dialog"
        aria-label="Artwork viewer"
      >
        <div className="flex items-center justify-between px-4 h-16 shrink-0">
          <span className="font-display text-smoke tracking-widest text-sm" data-testid="lightbox-counter">
            {String(current + 1).padStart(2, "0")} / {String(artworks.length).padStart(2, "0")}
          </span>
          <button onClick={onClose} data-testid="lightbox-close" aria-label="Close viewer" className="w-11 h-11 flex items-center justify-center text-bone hover:text-pink transition-colors duration-200">
            <X size={26} strokeWidth={1.5} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden" ref={emblaRef}>
          <div className="flex h-full">
            {artworks.map((a, i) => (
              <div key={a.id} className="flex-[0_0_100%] min-w-0 h-full flex items-center justify-center px-4">
                <img
                  src={imgUrl(a.images?.lg || a.images?.md)}
                  alt={artAlt(a)}
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  className="artwork-view max-h-full max-w-full object-contain"
                  data-testid={`lightbox-image-${i}`}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="shrink-0 px-5 pt-4 pb-6 border-t border-white/10 bg-ink">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="font-display text-2xl tracking-wide leading-none" data-testid="lightbox-title">
                {art?.title || "UNTITLED"}
              </p>
              {info && <p className="text-xs text-smoke mt-1.5">{info}</p>}
              {art?.original_available != null && (
                <p className="text-[10px] tracking-[0.25em] mt-2 text-smoke">
                  ORIGINAL {art.original_available ? <span className="text-acid">AVAILABLE</span> : "— SOLD / NOT AVAILABLE"}
                </p>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => emblaApi?.scrollPrev()} data-testid="lightbox-prev" aria-label="Previous" className="w-11 h-11 border border-white/15 flex items-center justify-center hover:border-pink hover:text-pink transition-colors duration-200">
                <ChevronLeft size={18} />
              </button>
              <button onClick={() => emblaApi?.scrollNext()} data-testid="lightbox-next" aria-label="Next" className="w-11 h-11 border border-white/15 flex items-center justify-center hover:border-pink hover:text-pink transition-colors duration-200">
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
          <Link
            to={`${inquireBase}?type=ORIGINAL%20ART&piece=${encodeURIComponent(art?.title || art?.slug || "")}`}
            data-testid="lightbox-inquire-button"
            className="mt-4 flex items-center justify-center h-12 bg-bone text-ink text-sm font-extrabold tracking-[0.2em] hover:bg-pink hover:text-white transition-colors duration-200"
          >
            INQUIRE ABOUT THIS PIECE
          </Link>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
